import { Link } from "wouter";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import LazyImage from "@/components/LazyImage";
import { IMAGES } from "@/lib/constants";
import RelatedArticles from "@/components/RelatedArticles";

const categories = [
  {
    title: "CO2 Lasrar",
    desc: "Moderna CO2 Lasrar för högprecisionsskärning i stål, rostfritt och aluminium.",
    machines: [
      { name: "Trumpf CO2 Laser 6 kW", details: "Skäryta 1500 x 3000 mm. Vår kraftfullaste laser för tjockare material." },
      { name: "Trumpf CO2 Laser 5 kW", details: "Skäryta 1500 x 3000 mm. Mångsidig maskin för de flesta uppdrag." },
      { name: "Amada CO2 Laser 5 kW", details: "Med automatisk plåtmatning (tassar som drar plåten). Utmärkt för små bitar." },
      { name: "Trumpf rörlaser 5 kW", details: "För skärning av profiler, fyrkantsprofiler och rör. Riktar sig mot konstruktionsstål." },
    ],
  },
  {
    title: "Kantpressar",
    desc: "Tre kantpressar för bockning av plåt med hög precision.",
    machines: [
      { name: "Ursviken kantpress 310 ton", details: "Vår största press. Hanterar tjockare material och längre bockningar." },
      { name: "Amada kantpress 160 ton", details: "Mångsidig press för medelstora jobb med god precision." },
      { name: "Ursviken kantpress 100 ton", details: "Snabb och effektiv för tunnare material och kortare serier." },
    ],
  },
  {
    title: "Avgradning och ytfinish",
    desc: "Automatisk avgradning och kantbrytning för felfri finish direkt efter laserskärning.",
    machines: [
      { name: "Lissmac SBM-L 1500 G1S2", details: "Avancerad gradningsmaskin som bearbetar båda sidor av plåten samtidigt. Arbetsbredd 1 500 mm, materialtjocklek 0,5–50 mm." },
    ],
  },
  {
    title: "Övrig utrustning",
    desc: "Kompletterande maskiner för en helhetslösning inom metallbearbetning.",
    machines: [
      { name: "Finn Power stansmaskin", details: "För stansning av hål och former i plåt." },
      { name: "Hedelius borr/fräs", details: "Stor borr- och fräsmaskin, nyligen inköpt för krävande uppdrag." },
      { name: "Svetsstation", details: "Utrustning för kvalificerad svetsning i flera material." },
      { name: "Pulverlackering", details: "Egen kapacitet för ytbehandling av färdiga komponenter." },
    ],
  },
];

const lissmacSpecs = [
  { label: "Modell", value: "SBM-L 1500 G1S2" },
  { label: "Arbetsbredd", value: "1 500 mm" },
  { label: "Materialtjocklek", value: "0,5–50 mm" },
  { label: "Bearbetning", value: "Båda sidor samtidigt" },
  { label: "Enheter", value: "3 ovan + 3 under" },
  { label: "Matningshastighet", value: "0–4 m/min (steglös)" },
  { label: "Effekt", value: "19,2 kW" },
  { label: "Vikt", value: "ca 2 800 kg" },
  { label: "Mått (B×D×H)", value: "3 300 × 1 500 × 1 800 mm" },
  { label: "Drift", value: "Torr drift" },
];

export default function Maskinpark() {
  return (
    <Layout>
      <SEOHead
        title="Maskinpark – CO2 Lasrar, kantpressar, Lissmac avgradning | ASPAB"
        description="ASPAB:s maskinpark: CO2 Lasrar upp till 6 kW, kantpressar upp till 310 ton, Lissmac SBM-L 1500 G1S2 gradningsmaskin, rörlaser och mer. Se vår kompletta utrustning."
        canonical="https://aspab.com/maskinpark"
        path="/maskinpark"
        keywords="maskinpark ASPAB, CO2 laser, kantpress, Lissmac SBM-L 1500 G1S2, gradningsmaskin, avgradning, laserskärning Eskilstuna"
        breadcrumbs={[{ name: "Maskinpark", href: "/maskinpark" }]}
              ogImage={IMAGES.ogMaskinpark}
      />

      <PageHero
        title="Maskinpark för precision och produktivitet"
        subtitle="Marknadens modernaste maskinpark. Kontinuerliga investeringar för att ligga i framkant med Trumpf, Amada och Lissmac-teknologi. Högre kvalitet och lägre kostnader för våra kunder."
        breadcrumbs={[{ name: "Maskinpark", href: "/maskinpark" }]}
        image={IMAGES.workshop4}
      />

      {/* Intro */}
      <section className="bg-white py-16 md:py-20 border-b border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-gray-500 text-lg leading-relaxed">
                ASPAB har byggt upp en maskinpark i absolut toppklass för att kunna leverera högsta kvalitet, 
                snabbaste ledtider och maximal kostnadseffektivitet. Vi investerar inte enligt scheman – vi 
                investerar när kundernas behov kräver det. På så sätt säkerställer vi att varje investering 
                direkt bidrar till ökad kundnytta.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Lissmac SBM-L 1500 G1S2 — Featured new investment */}
      <section className="bg-gray-50 py-20 md:py-28 border-b border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-[#2563EB]" />
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Ny investering</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-1">
              Lissmac SBM-L 1500 G1S2
            </h2>
            <p className="text-gray-500 mt-2 text-lg max-w-2xl">
              Avancerad gradningsmaskin för automatisk avgradning, kantbrytning och ytfinish av laserskurna, stansade och klippta plåtdetaljer.
            </p>
          </AnimatedSection>

          <div className="mt-12 grid lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection delay={100}>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <LazyImage
                  src={IMAGES.lissmacMaskin}
                  alt="Lissmac SBM-L 1500 G1S2 gradningsmaskin hos ASPAB i Eskilstuna"
                  className="w-full h-auto"
                  wrapperClassName="w-full"
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-3">Varför Lissmac?</h3>
                  <p className="text-gray-500 leading-relaxed">
                    Laserskurna plåtar får ibland vassa kanter på undersidan – så kallade grader. Dessa behöver avlägsnas 
                    innan delarna kan hanteras säkert och monteras korrekt. Lissmac SBM-L 1500 G1S2 löser detta genom att 
                    bearbeta båda sidor av plåten i ett enda genomdrag, vilket ger en jämn och hanteringssäker yta utan 
                    manuellt efterarbete.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-3">Fördelar för våra kunder</h3>
                  <ul className="space-y-2">
                    {[
                      "Gradfria kanter – säkrare hantering och bättre passform vid montering",
                      "Båda sidor bearbetas samtidigt – upp till 60 % kortare processtid",
                      "Jämn kantbrytning på invändiga och utvändiga konturer",
                      "Material: stål, rostfritt, aluminium och icke-järnmetaller",
                      "Arbetsbredd upp till 1 500 mm, materialtjocklek 0,5–50 mm",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-[#2563EB] mt-1 shrink-0" />
                        <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Specs table */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-3 bg-gray-100 border-b border-gray-200">
                    <h4 className="font-heading text-sm font-semibold text-gray-900 uppercase tracking-wider">Tekniska specifikationer</h4>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {lissmacSpecs.map((spec) => (
                      <div key={spec.label} className="flex justify-between px-5 py-2.5">
                        <span className="text-gray-500 text-sm">{spec.label}</span>
                        <span className="text-gray-900 text-sm font-medium">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  href="/nyheter/lissmac-gradningsmaskin"
                  className="inline-flex items-center gap-2 text-[#2563EB] font-medium hover:text-[#1D4ED8] transition-colors text-sm"
                >
                  Läs mer om vår Lissmac-investering <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Machine categories */}
      <section className="bg-white py-20 md:py-28">
        <div className="container space-y-20">
          {categories.map((cat, ci) => (
            <AnimatedSection key={cat.title} delay={ci * 100}>
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-px flex-1 bg-gradient-to-r from-[#2563EB]/30 to-transparent" />
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 shrink-0">{cat.title}</h2>
                  <div className="h-px flex-1 bg-gradient-to-l from-[#2563EB]/30 to-transparent" />
                </div>
                <p className="text-gray-500 text-center mb-8 max-w-xl mx-auto">{cat.desc}</p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {cat.machines.map((m) => (
                    <div key={m.name} className="bg-white border border-gray-200 rounded-lg p-5 hover:border-[#2563EB]/20 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                        <div>
                          <h3 className="text-gray-900 font-semibold">{m.name}</h3>
                          <p className="text-gray-500 text-sm mt-1 leading-relaxed">{m.details}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Key specs */}
      <section className="bg-gray-50 py-16 border-y border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { val: "6 kW", label: "Max lasereffekt" },
                { val: "310 ton", label: "Max presskraft" },
                { val: "25 mm", label: "Max skärtjocklek" },
                { val: "0,1 mm", label: "Tolerans" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-[#2563EB]">{s.val}</div>
                  <div className="text-gray-500 text-sm mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 text-center">
                Kontakta oss – maskinspecifik rådgivning inom 24h
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed text-center">
                Vill du veta mer om vår maskinpark eller bedöma kapaciteten för ditt projekt? 
                Vår specialist erbjuder er en kostnadsfri teknisk genomgång.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  "Skicka in ritning eller beskrivning av ditt projekt",
                  "Våra tekniker gör en kapacitets- och materialbedömning",
                  "Du får en offert och leveranstidsplan inom 24 timmar",
                ].map((step, i) => (
                  <div key={step} className="flex items-start gap-4 bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-[#2563EB] rounded-full text-white font-bold text-sm">
                      {i + 1}
                    </div>
                    <span className="text-gray-600 mt-1">{step}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors"
                >
                  Kontakta oss <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Kunskapsbank */}
      <RelatedArticles
        heading="Läs mer om vår teknik och utrustning"
        subheading="Fördjupa dig i våra maskiner, tekniker och hur vi arbetar med precision och kvalitet."
        articles={[
          { title: "Laserskärning med hög precision", slug: "laserskarning", href: "/tjanster/laserskarning", typeLabel: "Tjänst", ctaLabel: "Läs mer", excerpt: "Läs om ASPAB:s kapacitet, material, toleranser och leveranser inom industriell laserskärning." },
          { title: "Kantpressning: den kompletta guiden", slug: "kantpressning-guide", excerpt: "Allt du behöver veta om kantpressning – från grunderna till avancerade tekniker." },
          { title: "Automation och Industry 4.0", slug: "automation-metallbearbetning", excerpt: "Hur digitalisering och automation förändrar svensk plåtindustri." },
          { title: "Lissmac gradningsmaskin", slug: "lissmac-gradningsmaskin", excerpt: "Vår senaste investering för ännu bättre ytfinish på laserskurna detaljer." },
          { title: "Skärgaser vid laserskärning", slug: "laserskarning-aluminium", excerpt: "Hur valet av skärgas påverkar skärkvalitet, hastighet och materialkostnad." },
          { title: "Kvalitetskontroll inom metallbearbetning", slug: "forsta-bit-kontroll", excerpt: "Från första bit-kontroll till slutinspektion – så säkerställer vi kvalitet." },
        ]}
      />
    </Layout>
  );
}
